import {
  Badge,
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import React from "react";
import {Close} from "@mui/icons-material";
import {BaseWorldListItem} from "../lists/BaseWorldListItem";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {BaseWorldDocument} from "../../../../types/pj1dbApiWorld";
import {FetchMessage, Status, STATUSES} from "../../../../hooks/useFetchAllQuery";
import SearchForm, {SearchCond} from "./SearchForm";
import SortOrder from "./SortOrder";
import {BASE_WORLD_LIST_SORT_ORDERS, BaseWorldListSortOrderValue} from "../../../../constants/constants";
import CloseIconButton from "../buttons/CloseIconButton";
import Pjdb1ToolTip from "../../../../components/inputs/Pjdb1ToolTip";

export interface BaseWorldSelectionFieldProps extends Omit<BaseWorldListProps, 'onClick'> {
  onChange: (baseWorld: BaseWorldDocument|null) => void;
}

export function BaseWorldSelectionField({onChange, selectedBaseWorld, ...props}: BaseWorldSelectionFieldProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Stack gap={3}>
        <Box display='flex' flexWrap='nowrap' gap={2}>
          <Pjdb1Button
            label='ベースワールドを選択する'
            variant='outlined'
            color='inherit'
            onClick={() => setOpen(true)}
            sx={{width: 'fit-content'}}
          />
          <Pjdb1ToolTip
            toolTip='ベースワールドを選択するを押下し、ポップアップ画面でカスタムワールドのベースになるベースワールドを検索して選択してください。'/>
        </Box>
        <Box display='flex' flexWrap='wrap' width='100%' gap={3}>
          {selectedBaseWorld && (
            <Box key={selectedBaseWorld.attr['world-id']}>
              <Badge
                badgeContent={
                  <Close
                    fontSize='medium'
                    onClick={() => onChange(null)}
                    sx={{color: 'white', bgcolor: 'gray', borderRadius: 4}
                    }/>
                }>
                <BaseWorldListItem item={selectedBaseWorld}/>
              </Badge>
            </Box>
          )}
        </Box>
      </Stack>
      <Dialog disableEscapeKeyDown open={open} onClose={() => setOpen(false)} fullWidth maxWidth='xl'>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            ベースワールドを選択
            <CloseIconButton onClose={() => setOpen(false)}/>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor='#F6F7F8' py={4} px={2}>
            <BaseWorldList
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...props}
              selectedBaseWorld={selectedBaseWorld}
              onClick={(baseWorld) => {
                if (selectedBaseWorld?.attr['world-id'] === baseWorld.attr['world-id']) {
                  onChange(null);
                  return;
                }
                onChange(baseWorld);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Pjdb1Button label="閉じる" color='inherit' variant='contained' onClick={() => setOpen(false)}/>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface BaseWorldListProps {
  baseWorlds: BaseWorldDocument[];
  selectedBaseWorld: BaseWorldDocument | null;
  onClick: (baseWorld: BaseWorldDocument) => void;
  onChangeSearchCond: (searchCond: SearchCond<BaseWorldListSortOrderValue>) => void;
  fetchBaseWorlds: () => Promise<void>;
  searchCond: SearchCond<BaseWorldListSortOrderValue>;
  searchStatus: Status;
}

function BaseWorldList(props: BaseWorldListProps) {
  const selectedStyle: BoxProps['sx'] = {
    outlineColor: (theme) => theme.palette.primary.light,
    outlineStyle: "solid",
    outlineWidth: 6,
    borderRadius: 1,
  };

  return (
    <Stack gap={4}>
      <SearchForm
        label="ベースワールド名などを入力"
        isLoading={props.searchStatus === STATUSES.FETCHING}
        searchCond={props.searchCond}
        onChange={props.onChangeSearchCond}
        onSubmit={(e) => {
          e.preventDefault();
          props.fetchBaseWorlds().catch(console.error);
        }}
      />

      <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
          検索結果一覧
        </Typography>
        <Box display='flex' gap={2}>
          <Box>
            {props.baseWorlds.length > 0 ? `${props.baseWorlds.length.toLocaleString()}件` : ""}
          </Box>
          <SortOrder
            value={props.searchCond.sortOrder}
            onChange={(event) => {
              props.onChangeSearchCond({
                ...props.searchCond,
                sortOrder: event.target.value as BaseWorldListSortOrderValue
              });
            }}
            items={Object.values(BASE_WORLD_LIST_SORT_ORDERS)}/>
        </Box>
      </Box>

      {props.baseWorlds.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={2}>
          {props.baseWorlds.map((baseWorld) => (
            <BaseWorldListItem
              key={baseWorld.attr["world-id"]}
              item={baseWorld}
              onClick={() => props.onClick(baseWorld)}
              sx={{
                outlineColor: 'inherit',
                outlineStyle: "solid",
                outlineWidth: 1,
                borderRadius: 1,
                ...(
                  props.selectedBaseWorld?.attr["world-id"] === baseWorld.attr["world-id"]
                    ? selectedStyle
                    : {}
                )
              }}
            />
          ))}
        </Box>
      )}

      <FetchMessage data={props.baseWorlds} status={props.searchStatus}/>
    </Stack>
  )
}
